<template>
  <div class="box">
    <ConfirmDlg ref="dlgDel" title="Želite odstraniti uporabnika?" @confirm="delUser" />
    <div class="columns" style="align-items: flex-end">
      <div class="column col-6">
        <div class="box green">
          <div class="text-bold">#{{ data.user.id }} - {{ data.user.fullName }}</div>
          <div class="padding-top10">E-naslov: {{ data.user.email }}</div>
          <div>Tip uporabnika: {{ data.user.isB2b === true ? 'B2B' : 'Spletni' }}</div>
          <div>Vrsta prijave: {{ data.user.sipass ? 'SI-PASS' : 'KDP' }}</div>
          <div v-if="data.user.sipass">SI-PASS žeton: {{ data.user.sipassToken }}</div>
          <div>Datum registracije: {{ formatDate(data.user.dateCreate, 'DD.MM.YYYY HH:mm') }}</div>
          <div>Datum zadnje prijave: {{ formatDate(data.user.lastLogin, 'DD.MM.YYYY HH:mm') }}</div>
          <div>
            Št. napačnih prijav: <span :class="{ 'text-bold': data.user.numOfBadLogins > 0 }">{{ data.user.numOfBadLogins }}</span> <a href v-if="data.user.numOfBadLogins > 4" @click.prevent="unlock" class="text-bold">(odkleni)</a>
          </div>
          <div class="columns">
            <div class="column col-4">
              <div>
                <InputCheck v-model="data.user.active">Aktivnost</InputCheck>
              </div>
              <div>
                <InputCheck v-model="data.user.isB2b">B2B uporabnik</InputCheck>
              </div>
            </div>
            <div class="column col-4">
              <div>
                <InputCheck :disabled="$user.superAdmin !== true" v-model="data.user.admin">Administrator</InputCheck>
              </div>
              <div>
                <InputCheck :disabled="$user.superAdmin !== true" v-model="data.user.superAdmin">Super administrator</InputCheck>
              </div>
            </div>
          </div>
          <div class="padding-top20">
            <a class="button small" href @click.prevent="postData">Shrani</a> &nbsp; &nbsp;
            <a v-if="data.user.active === false && data.reportList.length === 0" class="button small red" href @click.prevent="dlgDel.open()">Briši</a>
          </div>
        </div>
      </div>
      <div class="column col-6 text-right small"></div>
    </div>
    <hr />
    <template v-if="!data.user.sipass">
      <div class="padding-top20 text-bold">Seznam digitalnih potrdil</div>
      <table class="table compact table-hover">
        <thead>
          <th>Serijska št.</th>
          <th>Izdajatelj</th>
          <th>Veljavnost</th>
          <th>Datum registracije</th>
        </thead>
        <tbody>
          <tr v-for="c in data.certList" :key="c.id">
            <td>{{ c.serial }}</td>
            <td>{{ c.issuer }}</td>
            <td>{{ c.validFromTo }}</td>
            <td>{{ formatDate(c.dateCreate, 'DD.MM.YYYY HH:mm') }}</td>
          </tr>
        </tbody>
      </table>
    </template>
    <div class="padding-top20 text-bold">Seznam naročnikov</div>
    <table class="table compact table-hover">
      <thead>
        <th>Naziv</th>
        <th>Matična št.</th>
        <th>Davčna št.</th>
        <th>Naslov</th>
        <th>Datum prijave</th>
      </thead>
      <tbody>
        <tr v-for="c in data.clientList" :key="c.id">
          <td>{{ c.title }}</td>
          <td>{{ c.idNumber }}</td>
          <td>{{ c.taxNumber }}</td>
          <td>{{ c.address }}, {{ c.city }}</td>
          <td>{{ formatDate(c.dateCreate, 'DD.MM.YYYY HH:mm') }}</td>
        </tr>
      </tbody>
    </table>
    <div class="padding-top20 text-bold">Seznam poročil <span class="small">(zadnjih 50)</span></div>
    <table class="table compact table-hover">
      <thead>
        <th>Pogodba</th>
        <th>Naročnik</th>
        <th>Tip</th>
        <th>Obdobje</th>
        <th>Datum poročanja</th>
      </thead>
      <tbody>
        <tr v-for="r in data.reportList" :key="r.id">
          <td>{{ r.contract.title }}</td>
          <td>{{ r.contract.client.title }}</td>
          <td>{{ r.reportType }}</td>
          <td>{{ formatDate(r.date, 'MMMM YYYY') }}</td>
          <td>{{ formatDate(r.dateUpdate, 'DD.MM.YYYY HH:mm') }}</td>
        </tr>
      </tbody>
    </table>
  </div>
  <div class="text-right padding-top20">
    <button class="button trans" @click="$router.push({ name: 'adminUserList' })">Nazaj</button>
  </div>
</template>

<script lang="ts">
import InputCheck from './common/InputCheck.vue'
import ConfirmDlg from '@/components/common/ConfirmDlg.vue'

import { useApi } from '@/lib/api'
import { formatDate } from '@/lib/filters'
import { defineComponent, ref, getCurrentInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { GlobalState } from '@/lib/types'
import { useAppViewedList } from '@/lib/common'

export default defineComponent({
  components: { InputCheck, ConfirmDlg },
  setup() {
    const { setRecord } = useAppViewedList()
    const _api = useApi()
    const _store = useStore<GlobalState>()
    const _route = useRoute()
    const _router = useRouter()
    const data = ref({ user: { id: null, active: null }, certList: [], clientList: [], reportList: [] })
    const dlgDel = ref()
    const _inst = getCurrentInstance()

    const getData = async () => {
      const res = await _api.get<any>('adminUser?id=' + _route.params.id)
      data.value = res.data
      setRecord('userList' + _route.params.id)
    }

    const postData = async () => {
      const url = _store.state.user.superAdmin === true ? `adminUserWrite` : 'adminUserWriteActive'
      const res = await _api.post<any>(url, data.value.user)
      _inst?.appContext.config.globalProperties.emitter.emit('showToaster', { theme: 'green', text: 'Podatki shranjeni' })
      data.value = res.data
    }

    const unlock = async () => {
      const res = await _api.get<any>(`adminUserWriteBadLogin?id=${data.value.user.id}`)
      _inst?.appContext.config.globalProperties.emitter.emit('showToaster', { theme: 'green', text: 'Uporabnik odklenjen.' })
      data.value = res.data
    }

    const delUser = async (c: boolean) => {
      if (c === true) {
        const res = await _api.delete<any>(`adminUserDelete?id=${data.value.user.id}`)
        if (res.status === 200) {
          _inst?.appContext.config.globalProperties.emitter.emit('showToaster', { theme: 'green', text: 'Uporabnik izbrisan.' })
          _router.push({ name: 'adminUserList' })
        }
      }
    }

    getData()

    return { data, formatDate, dlgDel, postData, unlock, delUser }
  }
})
</script>
